import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Plus, CheckCircle, Circle, Clock, AlertCircle, 
  X, Calendar, User, FileText, Flag, Search, Filter 
} from 'lucide-react';

const Aufgaben = () => {
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [newTask, setNewTask] = useState({
    titel: '',
    beschreibung: '',
    prioritaet: 'mittel',
    faelligAm: '',
    kategorie: 'allgemein',
    zugeordnet: ''
  });

  const [aufgaben, setAufgaben] = useState([
    {
      id: 1,
      titel: 'Gutachten für KH-091125-576 erstellen',
      beschreibung: 'Haftpflichtgutachten für Max Mustermann fertigstellen',
      status: 'offen',
      prioritaet: 'hoch',
      faelligAm: new Date(2025, 10, 25),
      kategorie: 'gutachten',
      zugeordnet: 'Max Mustermann',
      aktenzeichen: 'KH-091125-576',
      erstelltAm: new Date(2025, 10, 10)
    },
    {
      id: 2,
      titel: 'Kundendaten aktualisieren',
      beschreibung: 'Neue Adresse von Anna Schmidt in System eintragen',
      status: 'in_arbeit',
      prioritaet: 'mittel',
      faelligAm: new Date(2025, 10, 20),
      kategorie: 'verwaltung',
      zugeordnet: 'Anna Schmidt',
      erstelltAm: new Date(2025, 10, 8)
    },
    {
      id: 3,
      titel: 'Rechnung versenden',
      beschreibung: 'Rechnung für KH-071125-029 erstellen und versenden',
      status: 'erledigt',
      prioritaet: 'hoch',
      faelligAm: new Date(2025, 10, 15),
      kategorie: 'buchhaltung',
      zugeordnet: 'Qualid El Kadiri',
      aktenzeichen: 'KH-071125-029',
      erledigtAm: new Date(2025, 10, 14)
    },
    {
      id: 4,
      titel: 'Termin bestätigen',
      beschreibung: 'Besichtigungstermin mit Peter Wagner bestätigen',
      status: 'offen',
      prioritaet: 'niedrig',
      faelligAm: new Date(2025, 10, 18),
      kategorie: 'termine',
      zugeordnet: 'Peter Wagner',
      erstelltAm: new Date(2025, 10, 12)
    },
    {
      id: 5,
      titel: 'Fahrzeugschein scannen',
      beschreibung: 'Fehlende Fahrzeugscheine für 3 Akten nachscannen',
      status: 'in_arbeit',
      prioritaet: 'mittel',
      faelligAm: new Date(2025, 10, 22),
      kategorie: 'dokumentation',
      erstelltAm: new Date(2025, 10, 11)
    }
  ]);

  const handleCreateTask = () => {
    if (!newTask.titel) {
      alert('Bitte geben Sie einen Titel ein!');
      return;
    }

    const neueAufgabe = {
      id: aufgaben.length + 1,
      ...newTask,
      status: 'offen',
      faelligAm: newTask.faelligAm ? new Date(newTask.faelligAm) : null,
      erstelltAm: new Date()
    };

    setAufgaben([...aufgaben, neueAufgabe]);
    setShowNewTaskModal(false);
    setNewTask({
      titel: '',
      beschreibung: '',
      prioritaet: 'mittel',
      faelligAm: '',
      kategorie: 'allgemein',
      zugeordnet: ''
    });
  };

  const toggleTaskStatus = (taskId) => {
    setAufgaben(aufgaben.map(task => {
      if (task.id === taskId) {
        let newStatus;
        if (task.status === 'offen') newStatus = 'in_arbeit';
        else if (task.status === 'in_arbeit') newStatus = 'erledigt';
        else newStatus = 'offen';
        
        return {
          ...task,
          status: newStatus,
          erledigtAm: newStatus === 'erledigt' ? new Date() : null
        };
      }
      return task;
    }));
  };

  const getStatusConfig = (status) => {
    switch(status) {
      case 'offen':
        return {
          label: 'Offen',
          color: 'text-red-600 bg-red-50 border-red-200',
          icon: Circle
        };
      case 'in_arbeit':
        return {
          label: 'In Arbeit',
          color: 'text-blue-600 bg-blue-50 border-blue-200',
          icon: Clock
        };
      case 'erledigt':
        return {
          label: 'Erledigt',
          color: 'text-green-600 bg-green-50 border-green-200',
          icon: CheckCircle
        };
      default:
        return {
          label: status,
          color: 'text-gray-600 bg-gray-50 border-gray-200',
          icon: Circle
        };
    }
  };

  const getPriorityConfig = (prioritaet) => {
    switch(prioritaet) {
      case 'hoch':
        return { label: 'Hoch', color: 'text-red-600', icon: '🔴' };
      case 'mittel':
        return { label: 'Mittel', color: 'text-yellow-600', icon: '🟡' };
      case 'niedrig':
        return { label: 'Niedrig', color: 'text-green-600', icon: '🟢' };
      default:
        return { label: prioritaet, color: 'text-gray-600', icon: '⚪' };
    }
  };

  const getKategorieColor = (kategorie) => {
    const colors = {
      gutachten: 'bg-blue-100 text-blue-700',
      verwaltung: 'bg-purple-100 text-purple-700',
      buchhaltung: 'bg-green-100 text-green-700',
      termine: 'bg-yellow-100 text-yellow-700',
      dokumentation: 'bg-pink-100 text-pink-700',
      allgemein: 'bg-gray-100 text-gray-700'
    };
    return colors[kategorie] || colors.allgemein;
  };

  // Filter anwenden
  const filteredAufgaben = aufgaben.filter(task => {
    const matchesSearch = 
      task.titel.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.beschreibung?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.zugeordnet?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || task.prioritaet === filterPriority;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Statistiken
  const stats = {
    offen: aufgaben.filter(t => t.status === 'offen').length,
    inArbeit: aufgaben.filter(t => t.status === 'in_arbeit').length,
    erledigt: aufgaben.filter(t => t.status === 'erledigt').length,
    ueberfaellig: aufgaben.filter(t => 
      t.status !== 'erledigt' && 
      t.faelligAm && 
      t.faelligAm < new Date()
    ).length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Aufgaben</h1>
            <p className="text-gray-500 mt-1">Verwalten Sie Ihre Aufgaben und To-Dos</p>
          </div>
          <Button 
            onClick={() => setShowNewTaskModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Aufgabe
          </Button>
        </div>

        {/* Statistiken */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Circle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Offen</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.offen}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">In Arbeit</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.inArbeit}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Erledigt</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.erledigt}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Überfällig</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.ueberfaellig}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter und Suche */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <CardTitle>Aufgabenliste</CardTitle>
              <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full md:w-64"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Alle Status</option>
                  <option value="offen">Offen</option>
                  <option value="in_arbeit">In Arbeit</option>
                  <option value="erledigt">Erledigt</option>
                </select>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Alle Prioritäten</option>
                  <option value="hoch">Hoch</option>
                  <option value="mittel">Mittel</option>
                  <option value="niedrig">Niedrig</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredAufgaben.map(task => {
                const statusConfig = getStatusConfig(task.status);
                const priorityConfig = getPriorityConfig(task.prioritaet);
                const StatusIcon = statusConfig.icon;
                const isOverdue = task.status !== 'erledigt' && task.faelligAm && task.faelligAm < new Date();

                return (
                  <div
                    key={task.id}
                    className={`p-4 border rounded-lg hover:shadow-md transition-all ${
                      task.status === 'erledigt' ? 'bg-gray-50 opacity-75' : 'bg-white'
                    } ${isOverdue ? 'border-red-300' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleTaskStatus(task.id)}
                        className="mt-1 flex-shrink-0"
                      >
                        <StatusIcon className={`w-6 h-6 ${
                          task.status === 'erledigt' ? 'text-green-600' : 
                          task.status === 'in_arbeit' ? 'text-blue-600' : 
                          'text-gray-400 hover:text-gray-600'
                        }`} />
                      </button>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className={`font-semibold ${
                              task.status === 'erledigt' ? 'line-through text-gray-500' : 'text-gray-900'
                            }`}>
                              {task.titel}
                            </h3>
                            {task.beschreibung && (
                              <p className="text-sm text-gray-600 mt-1">{task.beschreibung}</p>
                            )}
                            
                            <div className="flex flex-wrap items-center gap-3 mt-3">
                              {/* Priorität */}
                              <div className="flex items-center space-x-1">
                                <span>{priorityConfig.icon}</span>
                                <span className={`text-xs font-medium ${priorityConfig.color}`}>
                                  {priorityConfig.label}
                                </span>
                              </div>

                              {/* Kategorie */}
                              <span className={`text-xs px-2 py-1 rounded ${getKategorieColor(task.kategorie)}`}>
                                {task.kategorie}
                              </span>

                              {/* Fälligkeitsdatum */}
                              {task.faelligAm && (
                                <div className={`flex items-center space-x-1 text-xs ${
                                  isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'
                                }`}>
                                  <Calendar className="w-3 h-3" />
                                  <span>{task.faelligAm.toLocaleDateString('de-DE')}</span>
                                  {isOverdue && <span className="text-red-600">⚠️ Überfällig</span>}
                                </div>
                              )}

                              {/* Zugeordnet */}
                              {task.zugeordnet && (
                                <div className="flex items-center space-x-1 text-xs text-gray-500">
                                  <User className="w-3 h-3" />
                                  <span>{task.zugeordnet}</span>
                                </div>
                              )}

                              {/* Aktenzeichen */}
                              {task.aktenzeichen && (
                                <div className="flex items-center space-x-1 text-xs text-gray-500">
                                  <FileText className="w-3 h-3" />
                                  <span>{task.aktenzeichen}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Status Badge */}
                          <div className={`ml-4 px-3 py-1 rounded border text-xs font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredAufgaben.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Keine Aufgaben gefunden</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Neue Aufgabe Modal */}
        {showNewTaskModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl mx-4">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>Neue Aufgabe</CardTitle>
                  <Button
                    onClick={() => setShowNewTaskModal(false)}
                    variant="ghost"
                    size="sm"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titel *
                    </label>
                    <Input
                      value={newTask.titel}
                      onChange={(e) => setNewTask({...newTask, titel: e.target.value})}
                      placeholder="z.B. Gutachten erstellen"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Beschreibung
                    </label>
                    <textarea
                      value={newTask.beschreibung}
                      onChange={(e) => setNewTask({...newTask, beschreibung: e.target.value})}
                      placeholder="Zusätzliche Informationen..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priorität
                      </label>
                      <select
                        value={newTask.prioritaet}
                        onChange={(e) => setNewTask({...newTask, prioritaet: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="niedrig">Niedrig</option>
                        <option value="mittel">Mittel</option>
                        <option value="hoch">Hoch</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kategorie
                      </label>
                      <select
                        value={newTask.kategorie}
                        onChange={(e) => setNewTask({...newTask, kategorie: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="allgemein">Allgemein</option>
                        <option value="gutachten">Gutachten</option>
                        <option value="verwaltung">Verwaltung</option>
                        <option value="buchhaltung">Buchhaltung</option>
                        <option value="termine">Termine</option>
                        <option value="dokumentation">Dokumentation</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fällig am
                      </label>
                      <Input
                        type="date"
                        value={newTask.faelligAm}
                        onChange={(e) => setNewTask({...newTask, faelligAm: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zugeordnet zu
                      </label>
                      <Input
                        value={newTask.zugeordnet}
                        onChange={(e) => setNewTask({...newTask, zugeordnet: e.target.value})}
                        placeholder="Name oder Akte"
                      />
                    </div>
                  </div>

                  <div className="flex space-x-2 pt-4">
                    <Button
                      onClick={() => setShowNewTaskModal(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleCreateTask}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      Aufgabe erstellen
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Aufgaben;
